import { Focusable, Tooltip, TooltipContent } from '@atlas/ui-primitives'
import type { ReactNode } from 'react'
import { notification } from '@/notifications/notification-store'

/**
 * Why a control is refused, in the words the reader needs.
 *
 * Every message names someone to approach rather than the rule that was broken:
 * "ask an administrator" is actionable, "requires ADMIN" is not. A test holds
 * that property, so a new entry cannot quietly drop it.
 */
export const REFUSAL = {
	upload: 'Uploading a file is available to a team member. Ask an administrator for access if you need to add files.',
	deleteFile:
		'A file can be removed by whoever uploaded it, or by an administrator. Ask an administrator to remove this one.',
	deleteComment:
		'A comment can be removed by whoever wrote it, or by an administrator. Ask an administrator to remove this one.',
	writeRule:
		'Rules decide what everyone else may produce, so they are written by an administrator. Ask an administrator to add or change a rule.',
	workOrder:
		'Creating and changing work orders is available to a team member. Ask an administrator for access if you need to do this.',
	review: 'Approving work, or sending it back, is a reviewer action. Ask an administrator to review this one.',
	verifyChangeRequest:
		'Verifying the fixes of a change request is a reviewer action. Ask an administrator to verify these.',
	raiseChangeRequest:
		'Raising a change request is a reviewer action. Ask an administrator to send this order back with changes.',
	configure: 'This is an administrator task. Ask an administrator to change it.',
} as const

/**
 * The one place the product decides how a control behaves when the signed-in
 * role may not use it. Wrap the control; change the policy here and every
 * surface changes with it.
 *
 * ```tsx
 * <Restricted allowed={canWork} why={REFUSAL.upload}>
 *   <Button onPress={goToUpload}>Upload &amp; classify</Button>
 * </Restricted>
 * ```
 *
 * The control stays on screen, dimmed, and says why on hover, on keyboard focus
 * and on press. It is not hidden, because a reader who cannot do something still
 * needs to learn it exists and who to ask. It is not `disabled` either: a
 * disabled control takes no pointer events, so it can never explain itself, and
 * a tooltip on it is unreachable by keyboard.
 *
 * The press is stopped in the capture phase, before the wrapped control sees it,
 * so the control itself needs no knowledge of any of this and keeps its ordinary
 * handler.
 */
export function Restricted({
	allowed,
	why,
	children,
	className,
}: {
	allowed: boolean
	why: string
	children: ReactNode
	/** Layout classes for the wrapper, when the control was filling its parent. */
	className?: string
}) {
	if (allowed) return <>{children}</>

	// React Aria presses fire from pointer events rather than from `click`, so
	// blocking `click` alone would let the action through. Pointer events are
	// swallowed silently and the explanation is attached to the click, which
	// fires once per press and also covers the keyboard's synthetic click.
	const swallow = (e: { preventDefault: () => void; stopPropagation: () => void }) => {
		e.preventDefault()
		e.stopPropagation()
	}
	const explain = (e: { preventDefault: () => void; stopPropagation: () => void }) => {
		swallow(e)
		notification.info(why)
	}

	return (
		<Tooltip>
			<Focusable>
				<span
					className={`inline-flex cursor-not-allowed opacity-50 ${className ?? ''}`}
					onPointerDownCapture={swallow}
					onPointerUpCapture={swallow}
					onClickCapture={explain}
				>
					{children}
				</span>
			</Focusable>
			<TooltipContent>{why}</TooltipContent>
		</Tooltip>
	)
}
